import { createContext, useContext, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  syncIntegration,
  resyncIntegration,
  getSyncStatus,
  getCompany,
} from '@/services/integrationsRedesignService';
import type { CompanyWithIntegrations } from '@/types/integrations-redesign';

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

interface StartFinanceSyncOptions {
  companyId: number;
  companyName: string;
  fullResync: boolean;
  onCoreComplete?: (updated: CompanyWithIntegrations) => void;
}

interface SyncNotificationContextValue {
  startFinanceSync: (options: StartFinanceSyncOptions) => void;
  isSyncInFlight: (companyId: number) => boolean;
}

const SyncNotificationContext = createContext<SyncNotificationContextValue | null>(null);

export function useSyncNotification() {
  const ctx = useContext(SyncNotificationContext);
  if (!ctx) throw new Error('useSyncNotification must be used within SyncNotificationProvider');
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const DEFERRED_POLL_INTERVAL_MS = 3_000;
const DEFERRED_POLL_TIMEOUT_MS = 5 * 60 * 1_000;

// apiFetch rejects with AppError, a plain object rather than an Error subclass.
function errorDescription(err: unknown): string | undefined {
  if (err instanceof Error) return err.message || undefined;
  if (typeof err === 'string') return err || undefined;
  if (err && typeof err === 'object') {
    const { message } = err as { message?: unknown };
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return undefined;
}

export function SyncNotificationProvider({ children }: { children: React.ReactNode }) {
  const inFlightRef = useRef<Set<number>>(new Set());

  const isSyncInFlight = useCallback((companyId: number) => {
    return inFlightRef.current.has(companyId);
  }, []);

  const startFinanceSync = useCallback(
    ({ companyId, companyName, fullResync, onCoreComplete }: StartFinanceSyncOptions) => {
      if (inFlightRef.current.has(companyId)) return;
      inFlightRef.current.add(companyId);

      const run = async () => {
        try {
          const result = fullResync
            ? await resyncIntegration(companyId, 'finance')
            : await syncIntegration(companyId, 'finance');

          if (!result.success) {
            toast.error(`${companyName}: Sync failed`, { description: result.message });
            inFlightRef.current.delete(companyId);
            return;
          }

          // Core pass succeeded — show immediate toast with counts
          toast.success(`${companyName}: Sync complete`, { description: result.message });

          // Notify caller so the sheet can refresh if still mounted
          if (result.updatedCompany && onCoreComplete) {
            onCoreComplete(result.updatedCompany);
          }

          // Poll for deferred steps completion
          pollDeferredCompletion(companyId, companyName, onCoreComplete);
        } catch (err) {
          toast.error(`${companyName}: Failed to sync finance integration`, {
            description: errorDescription(err),
          });
          inFlightRef.current.delete(companyId);
        }
      };

      run();
    },
    [],
  );

  function pollDeferredCompletion(
    companyId: number,
    companyName: string,
    onCoreComplete?: (updated: CompanyWithIntegrations) => void,
  ) {
    const startTime = Date.now();

    const intervalId = setInterval(async () => {
      if (Date.now() - startTime > DEFERRED_POLL_TIMEOUT_MS) {
        clearInterval(intervalId);
        inFlightRef.current.delete(companyId);
        return;
      }

      try {
        const status = await getSyncStatus(companyId);

        if (status.status === 'completed' || status.status === 'partial') {
          clearInterval(intervalId);
          inFlightRef.current.delete(companyId);

          toast.success(`${companyName}: Transactions and remaining data finished syncing.`);

          // Refresh company data if callback is available
          try {
            const refreshed = await getCompany(companyId);
            if (refreshed && onCoreComplete) {
              onCoreComplete(refreshed);
            }
          } catch {
            // Non-critical — toast was already shown
          }
        } else if (status.status === 'failed') {
          clearInterval(intervalId);
          inFlightRef.current.delete(companyId);

          toast.error(`${companyName}: Background sync failed`, {
            description: status.message,
          });
        }
        // "in-progress" or "none" → keep polling
      } catch {
        clearInterval(intervalId);
        inFlightRef.current.delete(companyId);
      }
    }, DEFERRED_POLL_INTERVAL_MS);
  }

  return (
    <SyncNotificationContext.Provider value={{ startFinanceSync, isSyncInFlight }}>
      {children}
    </SyncNotificationContext.Provider>
  );
}
